import { memo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { minDateToday, maxDateOneYearFromNow } from "../../model/schemas";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from "@/common/components";

export const WagonDeliveryCard = memo(() => {
  const form = useFormContext();
  const tradeEnd = useWatch({ control: form.control, name: "tradeEnd" });
  const minDeliveryDate = [minDateToday, tradeEnd?.slice(0, 10) ?? ""].sort().at(-1);
  const [scheduleByDecade, setScheduleByDecade] = useState(false);

  const handleDecadeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string | number) => void,
  ) => {
    const value = e.target.value;
    onChange(value);
    // Триггерим валидацию всех полей декад и totalWagons
    form.trigger(["decadeFirst", "decadeSecond", "decadeThird", "totalWagons"]);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>График подачи вагонов</CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="flex flex-col gap-3 md:gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <FormField
              control={form.control}
              name="deadlineAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Период действия заявки С *</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value || "");
                        form.trigger(["deadlineAt", "deadlineTo", "tradeEnd", "resultsDate"]);
                      }}
                      className="block"
                      min={minDeliveryDate}
                      max={maxDateOneYearFromNow}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="deadlineTo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Период действия заявки ПО *</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value || "");
                        form.trigger("deadlineTo");
                      }}
                      className="block"
                      min={minDateToday}
                      max={maxDateOneYearFromNow}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="md:col-span-3">
            <div className="p-4 border rounded-lg bg-muted/20">
              <div className="flex flex-col space-y-2">
                <FormLabel> График подачи вагонов (декада)</FormLabel>

                <div className="flex gap-2 items-center">
                  <FormField
                    control={form.control}
                    name="decade"
                    render={({ field }) => (
                      <Checkbox
                        id="decade"
                        checked={!!field.value}
                        onCheckedChange={(checked: boolean) => {
                          field.onChange(checked);
                          setScheduleByDecade(checked);
                        }}
                      />
                    )}
                  />
                  <div className="space-y-1 leading-none">
                    <FormLabel htmlFor="decade">
                      Включить управление графиком подачи по декадам
                    </FormLabel>
                  </div>
                </div>
              </div>

              {scheduleByDecade && (
                <div className="mt-5 md:mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_2fr_2fr] gap-3 md:gap-4">
                    <div className="text-Text-header-header_1 text-sm font-medium leading-5 content-start">
                      Количество вагонов по декадам
                    </div>
                    <div>
                      <FormField
                        control={form.control}
                        name="decadeFirst"
                        render={({ field }) => (
                          <>
                            <FormLabel>1-я декада месяца</FormLabel>
                            <Input
                              {...field}
                              type="number"
                              placeholder="0"
                              onChange={(e) => handleDecadeChange(e, field.onChange)}
                            />
                            <FormMessage />
                          </>
                        )}
                      />
                    </div>
                    <div>
                      <FormField
                        control={form.control}
                        name="decadeSecond"
                        render={({ field }) => (
                          <>
                            <FormLabel>2-я декада месяца</FormLabel>
                            <Input
                              {...field}
                              type="number"
                              placeholder="0"
                              onChange={(e) => handleDecadeChange(e, field.onChange)}
                            />
                            <FormMessage />
                          </>
                        )}
                      />
                    </div>
                    <div>
                      <FormField
                        control={form.control}
                        name="decadeThird"
                        render={({ field }) => (
                          <>
                            <FormLabel>3-я декада месяца</FormLabel>
                            <Input
                              {...field}
                              type="number"
                              placeholder="0"
                              onChange={(e) => handleDecadeChange(e, field.onChange)}
                            />
                            <FormMessage />
                          </>
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-3">
            <FormField
              control={form.control}
              name="decadeComments"
              render={({ field, fieldState: { invalid } }) => (
                <FormItem>
                  <FormLabel>Особые условия </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Дополнительные комментарии и особые требования..."
                      {...field}
                      value={field.value || ""}
                      rows={3}
                    />
                  </FormControl>
                  {invalid ? (
                    <FormMessage />
                  ) : (
                    <div className="text-Text-main text-xs font-normal font-['Montserrat'] leading-4">
                      до 500 символов
                    </div>
                  )}
                </FormItem>
              )}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
